import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
	ThumbUpAlt as ThumbUpFilledIcon,
	ThumbUpAltOutlined as ThumbUpIcon,
	ThumbDownAlt as ThumbDownFilledIcon,
	ThumbDownAltOutlined as ThumbDownIcon,
	ContentCopy as CopyIcon,
	Check as CheckIcon,
} from "@mui/icons-material";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { useFeedbackRow } from "../../hooks/widget/useFeedbackRow";
import { replyText } from "../../utils/feedback";
import FeedbackChips from "./FeedbackChips";

const PRIMARY = "#6257FF";

const iconButtonSx = (on) => ({
	width: 27,
	height: 27,
	borderRadius: "7px",
	border: "1px solid",
	borderColor: on ? PRIMARY : "rgba(0,0,0,0.10)",
	bgcolor: on ? PRIMARY : "#FFFFFF",
	color: on ? "#FFFFFF" : "rgba(0,0,0,0.6)",
	"&:hover": on
		? { bgcolor: "#4F46E5", borderColor: "#4F46E5" }
		: { bgcolor: "rgba(98,87,255,0.07)", borderColor: "rgba(98,87,255,0.3)" },
	"&.Mui-focusVisible": { outline: `2px solid ${PRIMARY}`, outlineOffset: 1 },
	"&.Mui-disabled": { opacity: 0.5, color: on ? "#FFFFFF" : "rgba(0,0,0,0.4)" },
	"& svg": { fontSize: 15 },
});

const visuallyHidden = {
	position: "absolute", width: 1, height: 1, p: 0, m: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

/**
 * 👍 👎 Copy under an AI reply (05a, FR-06 / FR-09 / FR-11 / FR-12). Rendered by Chat.jsx for every
 * AI reply with a server id (no feature flag — owner 2026-10-01); the hover / focus / touch
 * reveal lives on Chat's per-record wrapper (`.feedback-row`), and a row that holds a rating, an
 * error, the thanks line or an open popover stays visible on its own (`is-sticky`).
 */
export default function FeedbackRow({ record, conversationId, onCopy, copiedId }) {
	const { msg } = useWidgetMsg();
	const recordId = record?._id;
	const {
		rating, anchorEl, thanks, errorKey, busy, hidden, limited, onThumb, sendChips, closeChips,
	} = useFeedbackRow({ recordId, conversationId });

	if (hidden) return null;

	const copied = !!copiedId && copiedId === recordId;
	const sticky = !!(rating || errorKey || thanks || busy || anchorEl);
	const thumbsDisabled = busy || limited;
	const errorText = errorKey ? msg(`feedback.error_${errorKey}`, errorKey) : null;

	const thumb = (value, label, fallback, Filled, Outlined) => (
		<IconButton
			size="small"
			aria-label={msg(label, fallback)}
			aria-pressed={rating === value}
			data-rating={value}
			disabled={thumbsDisabled}
			onClick={(e) => onThumb(value, e.currentTarget)}
			sx={iconButtonSx(rating === value)}
		>
			{rating === value ? <Filled /> : <Outlined />}
		</IconButton>
	);

	return (
		<Box className={`feedback-row${sticky ? " is-sticky" : ""}`} sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-start" }}>
			<Box role="group" aria-label={msg("feedback.row_label", "Rate this answer")} sx={{ display: "flex", gap: 0.5 }}>
				{thumb("up", "feedback.helpful", "Helpful", ThumbUpFilledIcon, ThumbUpIcon)}
				{thumb("down", "feedback.not_helpful", "Not helpful", ThumbDownFilledIcon, ThumbDownIcon)}
				<Tooltip title={copied ? msg("feedback.copied", "Copied") : msg("feedback.copy", "Copy")}>
					<IconButton
						size="small"
						aria-label={msg("feedback.copy", "Copy")}
						data-action="copy"
						onClick={() => onCopy?.(replyText(record?.reply), recordId)}
						sx={iconButtonSx(false)}
					>
						{copied ? <CheckIcon /> : <CopyIcon />}
					</IconButton>
				</Tooltip>
				<Box component="span" role="status" sx={visuallyHidden}>{copied ? msg("feedback.copied", "Copied") : ""}</Box>
			</Box>
			{thanks && (
				<Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
					{msg("feedback.thanks", "Thanks — that helps us improve.")}
				</Typography>
			)}
			{errorText && !anchorEl && (
				<Typography role="alert" sx={{ fontSize: 12, color: "#d32f2f" }}>{errorText}</Typography>
			)}
			{anchorEl && (
				<FeedbackChips
					open
					anchorEl={anchorEl}
					rating={rating}
					busy={busy || limited}
					errorText={errorText}
					onSend={sendChips}
					onSkip={closeChips}
				/>
			)}
		</Box>
	);
}
